import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { roleLabel } from '../lib/roleLabel';
import { FormDialog } from '@ui-kit';
import { userCreateSchema, ROLE_OPTIONS } from '../schema/userSchema';
import type { UserCreateFormValues } from '../schema/userSchema';
import { useAssignableRoles } from '../hooks/useAssignableRoles';

interface UserCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: UserCreateFormValues) => void;
  isLoading?: boolean;
  errorMessage?: string;
}

/** R8-C-001: authSource is a separate state to avoid RHF radio setValueAs transform bug.
 *  'AUTO'   → generateTemporaryPassword = true  (자동 생성)
 *  'DIRECT' → generateTemporaryPassword = false (직접 입력)
 */
type AuthSource = 'AUTO' | 'DIRECT';

export function UserCreateDialog({
  open,
  onClose,
  onSubmit,
  isLoading = false,
  errorMessage,
}: UserCreateDialogProps) {
  const { t, i18n } = useTranslation();

  // R8-C-001: separate controlled state for password mode so conditional render is reliable
  const [authSource, setAuthSource] = useState<AuthSource>('AUTO');

  // 동적 역할 목록 — GET /api/eraf/roles
  const rolesQuery = useAssignableRoles();
  const assignableRoles = rolesQuery.data ?? [];

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<UserCreateFormValues>({
    resolver: zodResolver(userCreateSchema),
    defaultValues: {
      generateTemporaryPassword: true,
      role: 'ROLE_USER',
    },
  });

  // Keep RHF field in sync with authSource state
  useEffect(() => {
    setValue('generateTemporaryPassword', authSource === 'AUTO');
  }, [authSource, setValue]);

  // Reset form and authSource when dialog closes
  useEffect(() => {
    if (!open) {
      reset({ generateTemporaryPassword: true, role: 'ROLE_USER' });
      setAuthSource('AUTO');
    }
  }, [open, reset]);

  const handleFormSubmit = handleSubmit((values) => {
    onSubmit(values);
  });

  const fieldStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-1)',
    marginBottom: 'var(--spacing-3)',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    color: 'var(--color-text-secondary)',
  };

  const inputStyle: React.CSSProperties = {
    padding: 'var(--spacing-2) var(--spacing-3)',
    background: 'var(--color-surface-elevated)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-family-sans)',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  };

  const errorStyle: React.CSSProperties = {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-danger)',
    marginTop: 'var(--spacing-1)',
  };

  const hintStyle: React.CSSProperties = {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
    marginTop: 'var(--spacing-1)',
  };

  return (
    <FormDialog
      open={open}
      title={t('user.form.title.create')}
      onClose={onClose}
      onSubmit={() => void handleFormSubmit()}
      submitLabel={isLoading ? t('user.form.button.loading') : t('user.form.button.submit')}
      cancelLabel={t('user.form.button.cancel')}
    >
      <div>
        {/* 에러 배너 */}
        {errorMessage && (
          <div
            role="alert"
            style={{
              marginBottom: 'var(--spacing-3)',
              padding: 'var(--spacing-3)',
              background: 'color-mix(in srgb, var(--color-danger) 12%, var(--color-surface-muted))',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-danger)',
            }}
          >
            {errorMessage}
          </div>
        )}

        {/* 이메일 */}
        <div style={fieldStyle}>
          <label htmlFor="create-email" style={labelStyle}>
            {t('user.form.field.email')} *
          </label>
          <input
            id="create-email"
            type="email"
            placeholder={t('user.form.field.email.placeholder')}
            {...register('email')}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'create-email-error' : undefined}
            style={{
              ...inputStyle,
              borderColor: errors.email ? 'var(--color-danger)' : 'var(--color-surface-border)',
            }}
          />
          {errors.email && (
            <span id="create-email-error" style={errorStyle}>
              {errors.email.message}
            </span>
          )}
        </div>

        {/* 이름 */}
        <div style={fieldStyle}>
          <label htmlFor="create-displayName" style={labelStyle}>
            {t('user.form.field.displayName')} *
          </label>
          <input
            id="create-displayName"
            type="text"
            placeholder={t('user.form.field.displayName.placeholder')}
            {...register('displayName')}
            aria-invalid={!!errors.displayName}
            aria-describedby={errors.displayName ? 'create-displayName-error' : undefined}
            style={{
              ...inputStyle,
              borderColor: errors.displayName ? 'var(--color-danger)' : 'var(--color-surface-border)',
            }}
          />
          {errors.displayName && (
            <span id="create-displayName-error" style={errorStyle}>
              {errors.displayName.message}
            </span>
          )}
        </div>

        {/* 역할 — 동적 로드 (GET /api/eraf/roles), ROLE_OPTIONS 는 label fallback 용으로만 유지 */}
        <div style={fieldStyle}>
          <label htmlFor="create-role" style={labelStyle}>
            {t('user.form.field.role')} *
          </label>
          <select
            id="create-role"
            {...register('role')}
            aria-invalid={!!errors.role}
            aria-describedby={errors.role ? 'create-role-error' : undefined}
            aria-busy={rolesQuery.isLoading}
            disabled={rolesQuery.isLoading}
            style={{
              ...inputStyle,
              borderColor: errors.role ? 'var(--color-danger)' : 'var(--color-surface-border)',
              opacity: rolesQuery.isLoading ? 0.6 : 1,
            }}
          >
            {rolesQuery.isLoading && (
              <option value="">{t('user.role.loading')}</option>
            )}
            {!rolesQuery.isLoading && assignableRoles.length === 0
              // fallback: API 실패 시 하드코딩 목록으로 degradation
              ? ROLE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {t(`appshell.user.role.${opt.value.replace('ROLE_', '').toLowerCase()}`, { defaultValue: opt.value })}
                  </option>
                ))
              : assignableRoles.map((r) => (
                  <option key={r.code} value={r.code}>
                    {roleLabel(r, i18n.language)}
                  </option>
                ))
            }
          </select>
          {errors.role && (
            <span id="create-role-error" style={errorStyle}>
              {errors.role.message}
            </span>
          )}
        </div>

        {/* 초기 비밀번호 방식 — R8-C-001: use separate authSource state for reliable conditional render */}
        <div style={fieldStyle}>
          <span style={labelStyle}>{t('user.form.field.initialPassword')}</span>
          <div style={{ display: 'flex', gap: 'var(--spacing-4)', marginTop: 'var(--spacing-1)' }}>
            <label
              style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}
            >
              <input
                type="radio"
                name="authSource"
                value="AUTO"
                checked={authSource === 'AUTO'}
                onChange={() => setAuthSource('AUTO')}
                aria-label={t('user.form.field.initialPassword.auto')}
              />
              {t('user.form.field.initialPassword.auto')}
            </label>
            <label
              style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}
            >
              <input
                type="radio"
                name="authSource"
                value="DIRECT"
                checked={authSource === 'DIRECT'}
                onChange={() => setAuthSource('DIRECT')}
                aria-label={t('user.form.field.initialPassword.manual')}
              />
              {t('user.form.field.initialPassword.manual')}
            </label>
          </div>
        </div>

        {/* 비밀번호 직접 입력 — R8-C-001: conditionally rendered based on authSource state */}
        {authSource === 'DIRECT' && (
          <div style={fieldStyle}>
            <label htmlFor="create-initialPassword" style={labelStyle}>
              {t('user.form.field.initialPassword.placeholder')} *
            </label>
            <input
              id="create-initialPassword"
              type="password"
              placeholder={t('user.form.field.initialPassword.placeholder')}
              {...register('initialPassword')}
              aria-invalid={!!errors.initialPassword}
              aria-describedby={errors.initialPassword ? 'create-password-error' : undefined}
              style={{
                ...inputStyle,
                borderColor: errors.initialPassword ? 'var(--color-danger)' : 'var(--color-surface-border)',
              }}
            />
            {errors.initialPassword && (
              <span id="create-password-error" style={errorStyle}>
                {errors.initialPassword.message}
              </span>
            )}
          </div>
        )}

        {authSource === 'AUTO' && (
          <p style={hintStyle}>{t('user.form.hint.autoPassword')}</p>
        )}
      </div>
    </FormDialog>
  );
}
